import type { Command } from "../document/commands.ts";
import type { MapDocument, WorldDocument } from "../document/types.ts";
import { authoringOpenings, buildCollisionGrid, downsampleGrid, propOpenings } from "../compile/collision.ts";
import type { CompileContext } from "../compile/context.ts";
import { COLLISION_CELLS_PER_TILE, COMPILED_COLLISION_CELLS_PER_TILE } from "../document/types.ts";
import type { CollisionBrush } from "./adapter.ts";
import { collisionSourceAt, type CollisionSource } from "./collision-source.ts";
import { cellFootprint } from "./coords.ts";
import { ellipseCells, lineCells, rectCells } from "./shapes.ts";

/**
 * The shapes the collision tool paints with (ADR-040).
 */
export type CollisionShape = "pencil" | "square" | "ellipse" | "rect" | "line";

/**
 * Largest ellipse extent in cells (four tiles at quarter cells).
 */
export const MAX_ELLIPSE_CELLS = 16;
import { paintCollisionCommand } from "./panel-commands.ts";

/**
 * Collision painting (ADR-028): the chosen brush, painting cells of a collision layer as one command, and
 * the map's effective grid exactly as the compiler would build it from the live resolved layers.
 */

/**
 * What collision authoring needs from the store.
 */
export interface CollisionHost {
  readonly brushes: () => readonly CollisionBrush[];
  readonly declaredOpen: () => number | null;
  readonly document: () => WorldDocument;
  readonly map: () => MapDocument;
  readonly context: CompileContext;
  readonly layerCells: (layerId: string) => Uint32Array | null;
  readonly run: (command: Command) => boolean;
  readonly notify: () => void;
}

/**
 * The collision author.
 */
export class CollisionAuthoring {
  private chosen: CollisionBrush | null = null;
  private shapeChoice: CollisionShape = "square";
  private ellipse: { w: number; h: number } = { w: 4, h: 3 };
  private readonly shown = new Map<string, string>();
  private declared: number | null | undefined = undefined;

  /**
   * @param host The store's view of itself.
   */
  public constructor(private readonly host: CollisionHost) {}

  /**
   * The brush collision painting uses.
   *
   * @returns The brush, or null when the game declares none.
   */
  public get brush(): CollisionBrush | null {
    return this.chosen ?? this.host.brushes()[0] ?? null;
  }

  /**
   * The game's open mask (ADR-017's amendment of 2026-10-04): the bits of a painted collision byte that open a
   * cell to the terrain's rules.
   *
   * @returns The mask, 0 when the game declares none.
   */
  public get openMask(): number {
    return this.host.context.collision.open ?? 0;
  }

  /**
   * The collision brush that opens a cell, for the palette and the notices that offer it.
   *
   * @returns The brush, or null when the game declares none.
   */
  public get openBrush(): CollisionBrush | null {
    const open = this.declaredOpen() ?? this.openMask;
    return this.host.brushes().find((brush) => (brush.value & open) !== 0) ?? null;
  }

  /**
   * The open mask the game's rules declare now, asked of the host once: the rules are a function of the
   * context's terrains, which do not change while the store lives.
   *
   * @returns The mask, or null when the game's rules are not at hand.
   */
  private declaredOpen(): number | null {
    if (this.declared === undefined) {
      this.declared = this.host.declaredOpen();
    }
    return this.declared;
  }

  /**
   * Why the opening brush cannot be used on this world: its compile context was built before the game's rules
   * declared their present open mask. The editor says so at load and the palette disables the brush with it.
   *
   * @returns The reason, or null when the brush can be used or the game declares none.
   */
  public get openRefusal(): string | null {
    const declared = this.declaredOpen();
    const brush = this.openBrush;
    return declared === null || declared === this.openMask || brush === null ? null : `This world's build is older than the editor: compile it to use ${brush.name}`;
  }

  /**
   * Why a footprint's passage cells cannot be edited on this world: its compile context was built before the
   * game's rules declared their open mask, or by a build that laid no passage under a prop. The footprint editor
   * disables the cell kind with it, and the editor says so at load.
   *
   * @returns The reason, or null when they can be edited or the game declares no opening brush.
   */
  public get passageRefusal(): string | null {
    const brush = this.openBrush;
    if (brush === null) {
      return null;
    }
    return this.openRefusal ?? (this.host.context.collision.passages === true ? null : `This world's build is older than the editor: compile it to use ${brush.name}`);
  }

  /**
   * Chooses the collision brush. View state outside history.
   *
   * @param name Brush name.
   * @throws Error when the game declared no brush of that name, or the brush is the opening brush and
   *   `openRefusal` gives a reason.
   */
  public setBrush(name: string): void {
    const brush = this.host.brushes().find((b) => b.name === name);
    if (brush === undefined) {
      throw new Error(`no collision brush ${name}`);
    }
    const refusal = this.openRefusal;
    if (refusal !== null && brush.name === this.openBrush?.name) {
      throw new Error(refusal);
    }
    this.chosen = brush;
    this.host.notify();
  }

  /**
   * The collision brush shape (ADR-040).
   *
   * @returns The shape.
   */
  public get shape(): CollisionShape {
    return this.shapeChoice;
  }

  /**
   * Chooses the collision brush shape. View state outside history.
   *
   * @param shape The shape.
   */
  public setShape(shape: CollisionShape): void {
    this.shapeChoice = shape;
    this.host.notify();
  }

  /**
   * The state the collision overlay shows a behaviour prop in (ADR-040 amendment of 2026-10-03, P1.6-T74).
   *
   * @param propId The prop's id.
   * @returns The state chosen in its inspector, or null when it shows its initial state.
   */
  public shownStateOf(propId: string): string | null {
    return this.shown.get(propId) ?? null;
  }

  /**
   * Chooses the state the collision overlay shows a behaviour prop in, or clears it. View state outside
   * history, so it never undoes and is never saved.
   *
   * @param propId The prop's id.
   * @param stateId The state, or null for the initial state.
   */
  public setShownState(propId: string, stateId: string | null): void {
    if ((this.shown.get(propId) ?? null) === stateId) {
      return;
    }
    if (stateId === null) {
      this.shown.delete(propId);
    } else {
      this.shown.set(propId, stateId);
    }
    this.host.notify();
  }

  /**
   * The ellipse brush's extents in cells.
   *
   * @returns Width and height.
   */
  public get ellipseSize(): { readonly w: number; readonly h: number } {
    return this.ellipse;
  }

  /**
   * Sets the ellipse brush's extents. View state outside history.
   *
   * @param w Width in cells.
   * @param h Height in cells.
   * @throws Error when an extent is outside 1 to `MAX_ELLIPSE_CELLS`.
   */
  public setEllipse(w: number, h: number): void {
    for (const extent of [w, h]) {
      if (!Number.isInteger(extent) || extent < 1 || extent > MAX_ELLIPSE_CELLS) {
        throw new Error(`ellipse extent ${extent} is outside 1 to ${MAX_ELLIPSE_CELLS}`);
      }
    }
    this.ellipse = { w, h };
    this.host.notify();
  }

  /**
   * The collision cells one stamp of the current shape covers at a cell.
   *
   * @param cx Cell column in the collision grid.
   * @param cy Cell row.
   * @param brushSize Square brush size.
   * @returns Cell indices.
   */
  public stamp(cx: number, cy: number, brushSize: number): number[] {
    const { width, height } = this.gridSize();
    switch (this.shapeChoice) {
      case "pencil":
        return cellFootprint(cx, cy, 1, width, height);
      case "ellipse":
        return ellipseCells(cx, cy, this.ellipse.w, this.ellipse.h, width, height);
      default:
        return cellFootprint(cx, cy, brushSize, width, height);
    }
  }

  /**
   * The collision cells a drag covers for the rectangle and line shapes; empty for stamp shapes.
   *
   * @param x0 Start column.
   * @param y0 Start row.
   * @param x1 End column.
   * @param y1 End row.
   * @param brushSize Square brush size, the line's thickness.
   * @returns Cell indices.
   */
  public span(x0: number, y0: number, x1: number, y1: number, brushSize: number): number[] {
    const { width, height } = this.gridSize();
    if (this.shapeChoice === "rect") {
      return rectCells(x0, y0, x1, y1, width, height);
    }
    if (this.shapeChoice === "line") {
      return lineCells(x0, y0, x1, y1, brushSize, width, height);
    }
    return [];
  }

  /**
   * Whether the current collision shape paints on release from a drag.
   *
   * @returns True for rectangle and line.
   */
  public get drags(): boolean {
    return this.shapeChoice === "rect" || this.shapeChoice === "line";
  }

  /**
   * The collision grid's size in cells.
   *
   * @returns Width and height.
   */
  private gridSize(): { width: number; height: number } {
    const map = this.host.map();
    return { width: map.width * COLLISION_CELLS_PER_TILE, height: map.height * COLLISION_CELLS_PER_TILE };
  }

  /**
   * Paints cells of a collision layer as one command.
   *
   * @param layerId Collision layer id.
   * @param indices Cell indices.
   * @param value Byte to write; the current brush's value by default, 0 erases.
   * @throws Error when the layer is not a collision layer or no value is available.
   */
  public paintCells(layerId: string, indices: readonly number[], value?: number): boolean {
    const byte = value ?? this.brush?.value;
    if (byte === undefined) {
      throw new Error("the game declares no collision brush");
    }
    const command = paintCollisionCommand(this.host.map(), layerId, indices, byte);
    return command === null || this.host.run(command);
  }

  /**
   * Clears the painted cells of a collision layer that the rules and footprints already block, as one
   * command.
   *
   * @param layerId Collision layer id.
   * @returns How many cells were cleared.
   * @throws Error when the layer is not a collision layer.
   */
  public clearCovered(layerId: string): number {
    const map = this.host.map();
    const layer = map.layers.find((l) => l.id === layerId);
    if (layer === undefined || layer.kind !== "collision") {
      throw new Error(`layer ${layerId} is not a collision layer`);
    }
    const open = this.openMask;
    const derived = this.gridOf({ ...map, layers: map.layers.map((l) => (l.kind === "collision" ? { ...l, cells: l.cells.map((byte) => byte & open) } : l)) });
    const indices: number[] = [];
    for (let i = 0; i < layer.cells.length; i += 1) {
      if (layer.cells[i] !== 0 && ((layer.cells[i] as number) & ~(derived[i] as number)) === 0) {
        indices.push(i);
      }
    }
    return indices.length > 0 && this.paintCells(layerId, indices, 0) ? indices.length : 0;
  }

  /**
   * The collision bytes painted by hand into collision layers, without the rules' and footprints' share.
   *
   * @returns One byte per cell, the same size as `grid()`.
   */
  public painted(): Uint8Array {
    const map = this.host.map();
    const cells = new Uint8Array(map.width * map.height * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE);
    for (const layer of map.layers) {
      if (layer.kind === "collision" && layer.cells.length === cells.length) {
        for (let i = 0; i < cells.length; i += 1) {
          cells[i] = (cells[i] as number) | (layer.cells[i] as number);
        }
      }
    }
    return cells;
  }

  /**
   * The cells placed props open with their footprints' passages (ADR-017's amendment of 2026-10-05).
   *
   * @returns One byte per cell, the same size as `grid()`, or null when no prop opens a cell.
   */
  public propOpened(): Uint8Array | null {
    return authoringOpenings(this.host.document(), this.host.map(), this.host.context);
  }

  /**
   * The map's effective collision grid (ADR-028).
   *
   * @returns One byte per cell.
   */
  public grid(): Uint8Array {
    return this.gridOf(this.host.map());
  }

  /**
   * The map's effective collision grid at the compiled cell (ADR-113 decision 2): what the game plays on, which the
   * overlay draws.
   *
   * @returns One byte per compiled cell, `COMPILED_COLLISION_CELLS_PER_TILE` to a tile side.
   */
  public compiledGrid(): Uint8Array {
    return this.compiledGridOf(this.host.map());
  }

  /**
   * The cells placed props open with their footprints' passages, at the compiled cell.
   *
   * @returns One byte per compiled cell, the same size as `compiledGrid()`, or null when no prop opens a cell.
   */
  public compiledPropOpened(): Uint8Array | null {
    return propOpenings(this.host.document(), this.host.map(), this.host.context);
  }

  /**
   * What makes a collision cell block, so the editor can say why an erase removed nothing (ADR-045
   * amendment of 2026-09-24).
   *
   * @param index The cell's index in the collision grid.
   * @returns Paint on a collision layer, a prop, a tile or the rules, or the paint that opens the cell; null
   *   when the cell neither blocks nor is opened.
   * @throws RangeError when the index lies outside the grid.
   */
  public sourceAt(index: number): CollisionSource | null {
    return collisionSourceAt(this.host.document(), this.host.map(), this.host.context, index, () => this.grid(), this.shown);
  }

  /**
   * The effective collision grid of a map as given, over the live resolved layers.
   *
   * @param map The map, possibly with layers left out.
   * @returns One byte per cell.
   */
  private gridOf(map: MapDocument): Uint8Array {
    const side = COMPILED_COLLISION_CELLS_PER_TILE;
    return downsampleGrid(this.compiledGridOf(map), map.width * side, map.height * side, side / COLLISION_CELLS_PER_TILE);
  }

  /**
   * The effective collision grid of a map as given, at the compiled cell, over the live resolved layers.
   *
   * @param map The map, possibly with layers left out.
   * @returns One byte per compiled cell.
   */
  private compiledGridOf(map: MapDocument): Uint8Array {
    const layers = map.layers.flatMap((layer) => {
      if (layer.kind !== "terrain" && layer.kind !== "tile") {
        return [];
      }
      const tiles = this.host.layerCells(layer.id);
      return tiles === null ? [] : [{ id: layer.id, name: layer.name, variant: layer.kind === "terrain" ? layer.variant : null, tiles }];
    });
    return buildCollisionGrid(this.host.document(), map, { layers }, this.host.context, { states: this.shown });
  }
}
